/**
 * Finding a moment of the piece in seconds.
 *
 * Playing from what is selected, looping what is selected and following the music on the
 * page are all the same question asked three ways: where, in the played timeline, is this
 * piece of the document. The answer is looked up in the schedule rather than computed,
 * because the schedule is the only thing that knows what repeats did to the order of bars.
 *
 * What is selected arrives here as event ids rather than as a `Selection`, so that playback
 * stays independent of the editing layer: a cursor that follows a cue list or a script that
 * plays one phrase asks the same question without owning an editor.
 *
 * A bar played twice has two answers, and the earlier one is taken: pressing play on a bar
 * inside a repeat should start where the ear expects, at its first time through.
 *
 * The question also runs the other way, from a second back to a place in the bar, for a line
 * drawn on the page where the music is. Inside a bar time is read as even: the schedule
 * times bars and not beats, and a fermata in the bar is the one case where that is not so --
 * the line then crosses that bar a little slower all the way rather than stopping at the
 * sign. The two directions use the same arithmetic, so a place clicked and the line drawn
 * after the jump agree.
 */

import type { EventId, MeasureId } from "../document/ids";
import { capacityOf, locateEvent, measureIndexOf, measureOnset } from "../document/query";
import type { Score } from "../document/types";
import type { Schedule } from "./schedule";

/** A stretch of the played timeline, in seconds. */
export interface TimeRange {
  readonly from: number;
  readonly to: number;
}

/** When an event first sounds, or `null` when it never does -- a rest, or a tied end. */
export function startOfEvent(schedule: Schedule, eventId: EventId): number | null {
  let found: number | null = null;
  for (const note of schedule.notes) {
    if (note.eventId !== eventId) continue;
    if (found === null || note.start < found) found = note.start;
  }
  return found;
}

/** When a bar first sounds, or `null` when it is not in the played order. */
export function startOfBar(score: Score, schedule: Schedule, id: MeasureId): number | null {
  const index = measureIndexOf(score, id);
  if (index < 0) return null;
  return schedule.bars.find((bar) => bar.index === index)?.start ?? null;
}

/**
 * The stretch of time a set of events covers, or `null` when none of them sound.
 *
 * The end is the end of the last note rather than its beginning, so that looping a passage
 * plays the last note rather than cutting it off, and one selected note is a range as long
 * as that note.
 */
export function rangeOf(schedule: Schedule, events: Iterable<EventId>): TimeRange | null {
  const ids = new Set(events);
  let from = Number.POSITIVE_INFINITY;
  let to = Number.NEGATIVE_INFINITY;

  for (const note of schedule.notes) {
    if (!ids.has(note.eventId)) continue;
    from = Math.min(from, note.start);
    to = Math.max(to, note.start + note.duration);
  }

  return from === Number.POSITIVE_INFINITY ? null : { from, to };
}

/** Where to start playing for a set of events: the first that sounds, or the beginning. */
export function playFrom(schedule: Schedule, events: Iterable<EventId>): number {
  return rangeOf(schedule, events)?.from ?? 0;
}

/** A place in the written bars: which bar, and how far into it. */
export interface Moment {
  /** Index of the bar in the score, as the document numbers them. */
  readonly index: number;
  /** Ticks from the start of the bar. */
  readonly tick: number;
  /** How many ticks the bar holds, so a caller can tell how far along it is. */
  readonly length: number;
}

/** The seconds a played bar occupies: from its start to the next played bar's. */
function spanOf(schedule: Schedule, at: number): TimeRange | null {
  const bar = schedule.bars[at];
  if (bar === undefined) return null;
  return { from: bar.start, to: schedule.bars[at + 1]?.start ?? schedule.seconds };
}

/**
 * Where the music is at a second, or `null` before the first bar -- during a count-in -- and
 * after the last. A bar played twice answers with the same bar both times, since that is the
 * bar being played.
 */
export function momentAt(score: Score, schedule: Schedule, seconds: number): Moment | null {
  let at = -1;
  for (let next = 0; next < schedule.bars.length; next += 1) {
    if ((schedule.bars[next]?.start ?? Infinity) > seconds) break;
    at = next;
  }
  const bar = schedule.bars[at];
  const span = spanOf(schedule, at);
  if (bar === undefined || span === null || seconds > span.to) return null;
  const length = capacityOf(score, bar.index);
  const along = span.to > span.from ? (seconds - span.from) / (span.to - span.from) : 0;
  return { index: bar.index, tick: Math.min(length, along * length), length };
}

/**
 * When a place in a bar is first played, or `null` when the bar never is.
 *
 * `tick` is counted from the start of the bar, as a caret's onset is.
 */
export function secondsAt(
  score: Score,
  schedule: Schedule,
  index: number,
  tick: number,
): number | null {
  const at = schedule.bars.findIndex((bar) => bar.index === index);
  const span = spanOf(schedule, at);
  if (span === null) return null;
  const length = capacityOf(score, index);
  const along = length > 0 ? Math.max(0, Math.min(1, tick / length)) : 0;
  return span.from + along * (span.to - span.from);
}

/**
 * When an event is first reached, sounding or not.
 *
 * A note answers with when it sounds; a rest, or the tied end of a note, has no sound of its
 * own and answers with its place in the bar. That makes every drawn thing a place playing can
 * be moved to, which is what a click on the page during playback asks for.
 */
export function reachedAt(score: Score, schedule: Schedule, id: EventId): number | null {
  const sounds = startOfEvent(schedule, id);
  if (sounds !== null) return sounds;
  const where = locateEvent(score, id);
  if (where === null) return null;
  const tick = where.onset - measureOnset(score, where.measureIndex);
  return secondsAt(score, schedule, where.measureIndex, tick);
}
